"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const three_1 = require("three");
const FacesFromEdgesGenerator_1 = __importDefault(require("./FacesFromEdgesGenerator"));
// eslint-disable-next-line no-shadow
var surfacePosition;
(function (surfacePosition) {
    surfacePosition["FRONT"] = "front";
    surfacePosition["BACK"] = "back";
    surfacePosition["ON"] = "on";
})(surfacePosition || (surfacePosition = {}));
const FACE_KEYS = ['a', 'b', 'c'];
class GeometrySlicer {
    constructor(sourceGeometry, slicePlane, closeHolesFlag) {
        this.distances = [];
        this.positions = [];
        this.addedVertices = [];
        this.addedIntersections = [];
        this.newEdges = [[]];
        this.faceIndices = [];
        this.faceNormals = [];
        this.faceUvs = [];
        this.targetBufferVertices = [];
        this.targetBufferIndex = [];
        this.slicePlane = slicePlane;
        this.closeHolesFlag = closeHolesFlag;
        // Extract vertices from position attribute
        const posAttr = sourceGeometry.getAttribute('position');
        this.vertices = [];
        for (let i = 0; i < posAttr.count; i += 1) {
            this.vertices.push(new three_1.Vector3(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i)));
        }
        // Extract faces from index buffer (or sequential if non-indexed)
        const indexAttr = sourceGeometry.getIndex();
        this.faces = [];
        if (indexAttr) {
            for (let i = 0; i < indexAttr.count; i += 3) {
                this.faces.push({
                    a: indexAttr.getX(i),
                    b: indexAttr.getX(i + 1),
                    c: indexAttr.getX(i + 2),
                    vertexNormals: [],
                });
            }
        }
        else {
            for (let i = 0; i < posAttr.count; i += 3) {
                this.faces.push({ a: i, b: i + 1, c: i + 2, vertexNormals: [] });
            }
        }
        // Extract per-vertex normals if available, store as per-face vertex normals
        const normalAttr = sourceGeometry.getAttribute('normal');
        if (normalAttr) {
            for (const face of this.faces) {
                face.vertexNormals = [
                    new three_1.Vector3(normalAttr.getX(face.a), normalAttr.getY(face.a), normalAttr.getZ(face.a)),
                    new three_1.Vector3(normalAttr.getX(face.b), normalAttr.getY(face.b), normalAttr.getZ(face.b)),
                    new three_1.Vector3(normalAttr.getX(face.c), normalAttr.getY(face.c), normalAttr.getZ(face.c)),
                ];
            }
        }
        // Extract UVs: BufferGeometry stores UVs per-vertex, convert to per-face arrays
        const uvAttr = sourceGeometry.getAttribute('uv');
        if (uvAttr) {
            this.faceVertexUvs = [];
            for (const face of this.faces) {
                this.faceVertexUvs.push([
                    new three_1.Vector2(uvAttr.getX(face.a), uvAttr.getY(face.a)),
                    new three_1.Vector2(uvAttr.getX(face.b), uvAttr.getY(face.b)),
                    new three_1.Vector2(uvAttr.getX(face.c), uvAttr.getY(face.c)),
                ]);
            }
        }
        else {
            this.faceVertexUvs = null;
        }
    }
    sliceBufferGeometry() {
        return this.performSlice();
    }
    performSlice() {
        this.targetBufferGeometry = new three_1.BufferGeometry();
        this.vertices.forEach((vertex) => {
            const distance = this.findDistance(vertex, this.slicePlane);
            const position = this.distanceAsPosition(distance);
            this.distances.push(distance);
            this.positions.push(position);
        });
        this.faces.forEach((face, faceIndex) => {
            const facePositions = FACE_KEYS.map((key) => {
                return this.positions[face[key]];
            });
            if (facePositions.indexOf(surfacePosition.FRONT) === -1 && facePositions.indexOf(surfacePosition.BACK) !== -1) {
                return;
            }
            this.startFace(faceIndex);
            let lastKey = FACE_KEYS.at(-1);
            let lastIndex = face[lastKey];
            let lastDistance = this.distances[lastIndex];
            let lastPosition = this.positions[lastIndex];
            FACE_KEYS.forEach((key) => {
                const index = face[key];
                const distance = this.distances[index];
                const position = this.positions[index];
                if (position === surfacePosition.FRONT) {
                    if (lastPosition === surfacePosition.BACK) {
                        this.addIntersection(lastKey, key, lastDistance, distance);
                        this.addVertex(key);
                    }
                    else {
                        this.addVertex(key);
                    }
                }
                if (position === surfacePosition.ON) {
                    this.addVertex(key);
                }
                if (position === surfacePosition.BACK && lastPosition === surfacePosition.FRONT) {
                    this.addIntersection(lastKey, key, lastDistance, distance);
                }
                lastKey = key;
                lastIndex = index;
                lastPosition = position;
                lastDistance = distance;
            });
            this.endFace();
        });
        if (this.closeHolesFlag) {
            this.closeHoles();
        }
        this.targetBufferGeometry.setAttribute('position', new three_1.BufferAttribute(new Float32Array(this.targetBufferVertices), 3));
        this.targetBufferGeometry.setIndex(new three_1.Uint32BufferAttribute(this.targetBufferIndex, 1));
        return this.targetBufferGeometry;
    }
    // eslint-disable-next-line class-methods-use-this
    distanceAsPosition(distance) {
        if (distance < 0) {
            return surfacePosition.BACK;
        }
        if (distance > 0) {
            return surfacePosition.FRONT;
        }
        return surfacePosition.ON;
    }
    // eslint-disable-next-line class-methods-use-this
    findDistance(vertex, plane) {
        return plane.distanceToPoint(vertex);
    }
    startFace(sourceFaceIndex) {
        this.sourceFaceIndex = sourceFaceIndex;
        if (this.sourceFaceIndex !== undefined) {
            this.sourceFace = this.faces[this.sourceFaceIndex];
            this.sourceFaceUvs = this.faceVertexUvs ? this.faceVertexUvs[this.sourceFaceIndex] : null;
        }
        else {
            this.sourceFace = null;
            this.sourceFaceUvs = null;
        }
        this.faceIndices = [];
        this.faceNormals = [];
        this.faceUvs = [];
    }
    endFace() {
        const indices = this.faceIndices.map((index, i) => {
            return i;
        });
        this.addFace(indices);
    }
    closeHoles() {
        if (!this.newEdges[0].length) {
            return;
        }
        const facesFromEdgesGenerator = new FacesFromEdgesGenerator_1.default();
        facesFromEdgesGenerator.facesFromEdges(this.newEdges).forEach((faceIndices) => {
            const normal = this.faceNormal(faceIndices);
            if (normal.dot(this.slicePlane.normal) > 0.5) {
                faceIndices.reverse();
            }
            this.startFace();
            this.faceIndices = faceIndices;
            this.endFace();
        });
    }
    addVertex(key) {
        if (!this.sourceFace) {
            return;
        }
        this.addUv(key);
        this.addNormal(key);
        const index = this.sourceFace[key];
        let newIndex = null;
        // eslint-disable-next-line no-prototype-builtins
        if (this.addedVertices.hasOwnProperty(index)) {
            newIndex = this.addedVertices[index];
        }
        else {
            const vertex = this.vertices[index];
            this.targetBufferVertices.push(vertex.x, vertex.y, vertex.z);
            newIndex = Math.floor((this.targetBufferVertices.length - 1) / 3);
            this.addedVertices[index] = newIndex;
        }
        this.faceIndices.push(newIndex);
    }
    addIntersection(keyA, keyB, distanceA, distanceB) {
        if (!this.sourceFace) {
            return;
        }
        const t = Math.abs(distanceA) / (Math.abs(distanceA) + Math.abs(distanceB));
        this.addIntersectionUv(keyA, keyB, t);
        this.addIntersectionNormal(keyA, keyB, t);
        const indexA = this.sourceFace[keyA];
        const indexB = this.sourceFace[keyB];
        const id = this.intersectionId(indexA, indexB);
        let index;
        // eslint-disable-next-line no-prototype-builtins
        if (this.addedIntersections.hasOwnProperty(id)) {
            index = this.addedIntersections[id];
        }
        else {
            const vertexA = this.vertices[indexA];
            const vertexB = this.vertices[indexB];
            const newVertex = vertexA.clone().lerp(vertexB, t);
            this.targetBufferVertices.push(newVertex.x, newVertex.y, newVertex.z);
            index = Math.floor((this.targetBufferVertices.length - 1) / 3);
            this.addedIntersections[id] = index;
        }
        this.faceIndices.push(index);
        this.updateNewEdges(index);
    }
    addUv(key) {
        if (!this.sourceFaceUvs) {
            return;
        }
        const index = this.keyIndex(key);
        const uv = this.sourceFaceUvs[index];
        this.faceUvs.push(uv);
    }
    addIntersectionUv(keyA, keyB, t) {
        if (!this.sourceFaceUvs) {
            return;
        }
        const indexA = this.keyIndex(keyA);
        const indexB = this.keyIndex(keyB);
        const uvA = this.sourceFaceUvs[indexA];
        const uvB = this.sourceFaceUvs[indexB];
        const uv = uvA.clone().lerp(uvB, t);
        this.faceUvs.push(uv);
    }
    addNormal(key) {
        var _a;
        if (!((_a = this.sourceFace) === null || _a === void 0 ? void 0 : _a.vertexNormals.length)) {
            return;
        }
        const index = this.keyIndex(key);
        const normal = this.sourceFace.vertexNormals[index];
        this.faceNormals.push(normal);
    }
    addIntersectionNormal(keyA, keyB, t) {
        var _a;
        if (!((_a = this.sourceFace) === null || _a === void 0 ? void 0 : _a.vertexNormals.length)) {
            return;
        }
        const indexA = this.keyIndex(keyA);
        const indexB = this.keyIndex(keyB);
        const normalA = this.sourceFace.vertexNormals[indexA];
        const normalB = this.sourceFace.vertexNormals[indexB];
        const normal = normalA.clone().lerp(normalB, t).normalize();
        this.faceNormals.push(normal);
    }
    addFace(indices) {
        if (indices.length === 3) {
            this.addFacePart(indices[0], indices[1], indices[2]);
            return;
        }
        const pairs = [];
        for (let i = 0; i < indices.length; i += 1) {
            for (let j = i + 1; j < indices.length; j += 1) {
                const diff = Math.abs(i - j);
                if (diff > 1 && diff < indices.length - 1) {
                    pairs.push([indices[i], indices[j]]);
                }
            }
        }
        pairs.sort((pairA, pairB) => {
            const lengthA = this.faceEdgeLength(pairA[0], pairA[1]);
            const lengthB = this.faceEdgeLength(pairB[0], pairB[1]);
            return lengthA - lengthB;
        });
        const a = indices.indexOf(pairs[0][0]);
        // eslint-disable-next-line no-param-reassign
        indices = indices.slice(a).concat(indices.slice(0, a));
        const b = indices.indexOf(pairs[0][1]);
        const indicesA = indices.slice(0, b + 1);
        const indicesB = indices.slice(b).concat(indices.slice(0, 1));
        this.addFace(indicesA);
        this.addFace(indicesB);
    }
    addFacePart(a, b, c) {
        this.targetBufferIndex.push(this.faceIndices[a], this.faceIndices[b], this.faceIndices[c]);
    }
    faceEdgeLength(a, b) {
        const indexA = this.faceIndices[a];
        const indexB = this.faceIndices[b];
        const indA = 3 * indexA;
        const vertexA = new three_1.Vector3(this.targetBufferVertices[indA], this.targetBufferVertices[indA + 1], this.targetBufferVertices[indA + 2]);
        const indB = 3 * indexB;
        const vertexB = new three_1.Vector3(this.targetBufferVertices[indB], this.targetBufferVertices[indB + 1], this.targetBufferVertices[indB + 2]);
        return vertexA.distanceToSquared(vertexB);
    }
    // eslint-disable-next-line class-methods-use-this
    intersectionId(indexA, indexB) {
        return [indexA, indexB].sort((a, b) => a - b).join(',');
    }
    // eslint-disable-next-line class-methods-use-this
    keyIndex(key) {
        return FACE_KEYS.indexOf(key);
    }
    updateNewEdges(index) {
        const edgeIndex = this.newEdges.length - 1;
        const edge = this.newEdges[edgeIndex];
        if (edge.length < 2) {
            edge.push(index);
        }
        else {
            this.newEdges.push([index]);
        }
    }
    faceNormal(faceIndices) {
        const vertices = faceIndices.map((index) => {
            const ind = 3 * index;
            return new three_1.Vector3(this.targetBufferVertices[ind], this.targetBufferVertices[ind + 1], this.targetBufferVertices[ind + 2]);
        });
        const edgeA = vertices[0].clone().sub(vertices[1]);
        const edgeB = vertices[0].clone().sub(vertices[2]);
        return edgeA.cross(edgeB).normalize();
    }
}
exports.default = GeometrySlicer;
